import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Check as CheckIcon,
  Stars as StarsIcon,
  Rocket as RocketIcon,
  Diamond as DiamondIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { getApiUrl } from '../config';
import PageHead from '../components/PageHead';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$25',
    icon: <StarsIcon sx={{ fontSize: 40 }} />,
    color: '#2196F3',
    features: [
      '7 AI-Generated Posts per Month',
      'Auto SEO Optimization',
      'AI Generated Images',
      'Writing Style Analysis',
      'Custom Scheduling',
      'WordPress Auto-Posting',
    ],
  },
  {
    id: 'standard',
    name: 'Standard',
    price: '$50',
    icon: <RocketIcon sx={{ fontSize: 40 }} />,
    color: '#9C27B0',
    features: [
      '15 AI-Generated Posts per Month',
      'Auto SEO Optimization',
      'AI Generated Images',
      'Writing Style Analysis',
      'Custom Scheduling',
      'WordPress Auto-Posting',
    ],
    popular: true,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '$90',
    icon: <DiamondIcon sx={{ fontSize: 40 }} />,
    color: '#F50057',
    features: [
      '30 AI-Generated Posts per Month',
      'Auto SEO Optimization',
      'AI Generated Images',
      'Writing Style Analysis',
      'Custom Scheduling',
      'WordPress Auto-Posting',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Contact Us',
    icon: <BusinessIcon sx={{ fontSize: 40 }} />,
    color: '#333',
    features: [
      'Custom Post Volume',
      'Auto SEO Optimization',
      'AI Generated Images',
      'Writing Style Analysis',
      'Custom Scheduling',
      'WordPress Auto-Posting',
      'Multiple Blog Management',
      'API Access',
    ],
    enterprise: true,
  },
];

const SubscriptionUpgrade = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentPlan = searchParams.get('current') || 'basic';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSelectPlan = async (planId) => {
    if (planId === 'enterprise') {
      // For enterprise, redirect to contact form or open email
      window.location.href = 'mailto:sales@yourdomain.com?subject=Enterprise Plan Inquiry';
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Call your backend to create a Stripe checkout session
      console.log('Creating checkout session for plan:', planId);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const response = await fetch(`${getApiUrl()}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_type: planId
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
      
      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.checkout_url) {
        throw new Error('No checkout URL received from server');
      }

      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Add loading state to the UI
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <PageHead 
        title="Upgrade Plan" 
        description="Choose the perfect AutoBlogPilot plan for your blogging needs"
      />
      <Box sx={{ py: 8 }}>
        <Typography
          variant="h2"
          component="h1"
          textAlign="center"
          sx={{
            mb: 2,
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Upgrade Your Plan
        </Typography>
        <Typography
          variant="h5"
          textAlign="center"
          color="text.secondary"
          sx={{ mb: 6 }}
        >
          Choose the perfect plan for your blogging needs
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan) => (
            <Grid item xs={12} md={4} key={plan.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Paper
                  elevation={plan.popular ? 12 : 4}
                  sx={{
                    p: 4,
                    height: '100%',
                    position: 'relative',
                    borderRadius: 4,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                    },
                    border: plan.popular ? `2px solid ${plan.color}` : 'none',
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="MOST POPULAR"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 24,
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        color: plan.color,
                        mb: 2,
                      }}
                    >
                      {plan.icon}
                    </Box>
                    <Typography variant="h4" component="h2" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Typography
                      variant="h3"
                      component="p"
                      sx={{ color: plan.color }}
                    >
                      {plan.price}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      per month
                    </Typography>
                  </Box>

                  <List>
                    {plan.features.map((feature) => (
                      <ListItem key={feature} disableGutters>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <CheckIcon sx={{ color: plan.color }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    variant={plan.popular ? 'contained' : 'outlined'}
                    fullWidth
                    size="large"
                    sx={{
                      mt: 4,
                      bgcolor: plan.popular ? plan.color : 'transparent',
                      borderColor: plan.color,
                      '&:hover': {
                        bgcolor: plan.popular ? plan.color : 'rgba(0,0,0,0.04)',
                        borderColor: plan.color,
                      },
                    }}
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={currentPlan === plan.id}
                  >
                    {currentPlan === plan.id
                      ? 'Current Plan'
                      : plan.enterprise
                      ? 'Contact Sales'
                      : `Upgrade to ${plan.name}`}
                  </Button>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {loading && (
          <CircularProgress sx={{ mt: 2 }} />
        )}
      </Box>
    </Container>
  );
};

export default SubscriptionUpgrade; 